import React, { useState, useContext, useEffect } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Button,
    Box,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Badge,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { AppContext } from "../context/appContext";
import { getTotalCartItems, fetchCart, resetCart } from "../redux/shopSlice";
import logo from "./assets/logo.png";

const Navbar = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const totalItems = useSelector(getTotalCartItems);
    const { userData, backendUrl, setUserData, setIsLoggedIn } =
        useContext(AppContext);

    useEffect(() => {
        dispatch(fetchCart({ page: 0, limit: 10 }));
    }, [dispatch]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const logout = async () => {
        try {
            const { data } = await axios.post(backendUrl + "/api/auth/logout");
            if (data.success) {
                dispatch(resetCart());
                setIsLoggedIn(false);
                setUserData(false);
                navigate("/");
                toast.success(data.message);
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    const menuItems = [
        { name: "Shop", path: "/" },
        { name: "Mens", path: "/men" },
        { name: "Women", path: "/women" },
        { name: "Kids", path: "/kid" },
    ];

    const drawer = (
        <Box
            sx={{
                width: 250,
                textAlign: "center",
                backgroundColor: "#fff",
                height: "100%",
            }}
            role="presentation"
            onClick={handleDrawerToggle}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderBottom: "1px solid #ddd",
                }}
            >
                <Typography variant="h6">Menu</Typography>
                <IconButton>
                    <CloseIcon />
                </IconButton>
            </Box>
            <List>
                {menuItems.map((item) => (
                    <ListItem
                        button
                        key={item.name}
                        component={NavLink}
                        to={item.path}
                        sx={{
                            "&.active .MuiListItemText-root": { color: "#FF4141" },
                            textAlign: "center",
                        }}
                    >
                        <ListItemText primary={item.name} />
                    </ListItem>
                ))}
                <ListItem>
                    {userData ? (
                        <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            onClick={logout}
                            sx={{ borderRadius: "20px" }}
                        >
                            Logout
                        </Button>
                    ) : (
                        <Button
                            fullWidth
                            variant="outlined"
                            component={Link}
                            to="/login"
                            sx={{ borderRadius: "20px" }}
                        >
                            Login
                        </Button>
                    )}
                </ListItem>
            </List>
        </Box>
    );

    return (
        <>
            <AppBar
                position="sticky"
                sx={{
                    background: "#fff",
                    color: "#000",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    zIndex: 1300,
                }}
            >
                <Toolbar sx={{ justifyContent: "space-between" }}>
                    {/* Left - Logo */}
                    <Box
                        sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                        onClick={() => navigate("/")}
                    >
                        <img src={logo} alt="Logo" style={{ width: 120, height: "auto" }} />
                    </Box>

                    {/* Center - Menu Links (hidden on mobile) */}
                    <Box
                        sx={{
                            display: { xs: "none", md: "flex" },
                            gap: 4,
                        }}
                    >
                        {menuItems.map((item) => (
                            <NavLink
                                key={item.name}
                                to={item.path}
                                style={({ isActive }) => ({
                                    textDecoration: "none",
                                    color: isActive ? "#FF4141" : "#515151",
                                    fontWeight: 500,
                                    fontSize: "18px",
                                    position: "relative",
                                })}
                            >
                                {item.name}
                            </NavLink>
                        ))}
                    </Box>

                    {/* Right - Buttons */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {userData ? (
                            <Button
                                variant="outlined"
                                onClick={logout}
                                sx={{
                                    borderRadius: "25px",
                                    px: 3,
                                    display: { xs: "none", md: "block" },
                                }}
                            >
                                Logout
                            </Button>
                        ) : (
                            <Button
                                variant="outlined"
                                component={Link}
                                to="/login"
                                sx={{
                                    borderRadius: "25px",
                                    px: 3,
                                    display: { xs: "none", md: "block" },
                                }}
                            >
                                Login
                            </Button>
                        )}

                        <IconButton
                            component={Link}
                            to="/cart"
                            sx={{ color: "#000", position: "relative" }}
                        >
                            <Badge
                                badgeContent={totalItems}
                                color="error"
                                overlap="circular"
                            >
                                <ShoppingCartOutlinedIcon />
                            </Badge>
                        </IconButton>

                        {/* Mobile Menu Button */}
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="end"
                            onClick={handleDrawerToggle}
                            sx={{ display: { md: "none" } }}
                        >
                            <MenuIcon />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Drawer for mobile menu */}
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                sx={{
                    "& .MuiDrawer-paper": {
                        boxSizing: "border-box",
                        width: 250,
                    },
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
};

export default Navbar;